import type { DeepActivityEvent, OrchestratorReasoningBlock } from "@/stores/ai-explorer-store"

export interface LaneEntry {
  /** Stable key — `${source}:${dispatchId ?? "main"}`. Unique within a turn. */
  key: string
  source: string
  dispatchId: string | null
  events: DeepActivityEvent[]
}

export interface PairedTool {
  id: string
  tool: string
  source: string
  start: DeepActivityEvent | null
  end: DeepActivityEvent | null
}

export interface LaneStatus {
  running: number
  errored: number
  total: number
  /** Latest paired tool (running or completed) — drives the headline phrase. */
  latest: PairedTool | null
  /** Elapsed seconds across the lane (first-start → last-end), or null. */
  elapsedSeconds: number | null
}

/**
 * Group events into lanes, one per unique (source, dispatch_id). Main-agent
 * events (source = "deep_explorer") collapse into a single lane regardless
 * of dispatch — the main agent doesn't have a dispatch concept.
 */
export function buildLanes(events: DeepActivityEvent[]): LaneEntry[] {
  const lanes = new Map<string, LaneEntry>()
  const order: string[] = []
  for (const e of events) {
    if ((e.event as unknown as string) === "truncated") continue
    const isMain = e.source === "deep_explorer"
    const key = isMain ? "deep_explorer" : `${e.source}:${e.dispatch_id ?? "unknown"}`
    let lane = lanes.get(key)
    if (!lane) {
      lane = {
        key,
        source: e.source,
        dispatchId: isMain ? null : (e.dispatch_id ?? null),
        events: [],
      }
      lanes.set(key, lane)
      order.push(key)
    }
    lane.events.push(e)
  }
  return order.map((k) => lanes.get(k)!)
}

export function laneStatus(paired: PairedTool[]): LaneStatus {
  let running = 0
  let errored = 0
  let firstStartMs: number | null = null
  let lastEndMs: number | null = null
  let stillRunningSeen = false
  let latest: PairedTool | null = null
  for (const p of paired) {
    if (!p.end) running++
    if (p.end?.result_summary?.success === false) errored++
    latest = p
    const startTs = p.start?.timestamp
    if (startTs) {
      const ms = Date.parse(startTs)
      if (Number.isFinite(ms)) {
        if (firstStartMs == null || ms < firstStartMs) firstStartMs = ms
      }
    }
    const endTs = p.end?.timestamp
    if (endTs) {
      const ms = Date.parse(endTs)
      if (Number.isFinite(ms)) {
        if (lastEndMs == null || ms > lastEndMs) lastEndMs = ms
      }
    } else {
      stillRunningSeen = true
    }
  }
  let elapsedSeconds: number | null = null
  if (firstStartMs != null) {
    const end = stillRunningSeen ? Date.now() : lastEndMs
    if (end != null && end >= firstStartMs) elapsedSeconds = (end - firstStartMs) / 1000
  }
  return { running, errored, total: paired.length, latest, elapsedSeconds }
}

/**
 * A unified item in the orchestrator timeline: a paired tool, an
 * orchestrator reasoning block, or a status event. Used by the lane render
 * to interleave the three kinds chronologically.
 */
export type LaneItem =
  | { kind: "tool"; ts: number; paired: PairedTool }
  | { kind: "reasoning"; ts: number; block: OrchestratorReasoningBlock }
  | { kind: "status"; ts: number; status: DeepActivityEvent }

/**
 * Phase 4 (workspace-focus) — derive the artifact id (if any) a lane entry
 * correlates to. Backend Phase 2.5 propagates `artifact_id` onto the
 * `result_summary` of tool-call ends and onto status events at the
 * plan-ready / deliverable-ready emission sites; some tools also stamp it
 * on `input_summary` (e.g. when the call's *input* references the artifact
 * being acted on). Reasoning blocks have no correlation today.
 *
 * Priority order (matches the brief):
 *   1. tool: `paired.start.result_summary.artifact_id`
 *   2. tool: `paired.end.result_summary.artifact_id`
 *   3. tool: `paired.start.input_summary.artifact_id`
 *   4. status: underlying event's `result_summary.artifact_id`
 *   5. reasoning: `null` (no correlation)
 *
 * The `result_summary` and `input_summary` types are intentionally loose
 * (`Record<string, unknown>`) — we narrow inline at the read sites rather
 * than tighten the type, so the wire schema can carry whatever the backend
 * sends without the frontend needing a coordinated type bump.
 */
export function getArtifactIdFromLaneItem(item: LaneItem): string | null {
  if (item.kind === "reasoning") return null
  if (item.kind === "status") {
    const summary = item.status.result_summary
    const id = summary?.["artifact_id"]
    return typeof id === "string" && id.length > 0 ? id : null
  }
  // kind === "tool"
  const start = item.paired.start
  const end = item.paired.end
  const fromStartResult = start?.result_summary?.["artifact_id"]
  if (typeof fromStartResult === "string" && fromStartResult.length > 0) return fromStartResult
  const fromEndResult = end?.result_summary?.["artifact_id"]
  if (typeof fromEndResult === "string" && fromEndResult.length > 0) return fromEndResult
  const fromStartInput = start?.input_summary?.["artifact_id"]
  if (typeof fromStartInput === "string" && fromStartInput.length > 0) return fromStartInput
  return null
}
